import { motion } from "framer-motion";
import { B } from "../data/pillars";
import SectionHeader from "./SectionHeader";

export default function EbitdaImpact({ avgScore }) {
  // Determine which band the user is currently in
  const isReactive = avgScore < 3;
  const isScaling = avgScore >= 3 && avgScore < 4;
  const isOptimized = avgScore >= 4;

  const bands = [
    {
      title: "OML 1–2: Reactive / Emerging",
      impact: "-3% to -5% EBITDA Drag",
      color: B.red,
      active: isReactive,
      desc: "High variable costs, shadow IT, unplanned downtime, and manual workarounds actively erode profitability compared to industry peers."
    },
    {
      title: "OML 3: Scaling",
      impact: "Baseline EBITDA",
      color: B.yellow,
      active: isScaling,
      desc: "Predictable IT OpEx. Standardized tools and managed services stabilize costs, plugging financial leaks and bringing margins to industry baseline."
    },
    {
      title: "OML 4–5: Proactive / Optimized",
      impact: "+5% to +10% EBITDA Expansion",
      color: B.green,
      active: isOptimized,
      desc: "Zero-touch automation, AI-integrated workflows, and strategic tech alignment reduce SG&A and drive scalable revenue growth without proportional headcount increases."
    }
  ];

  return (
    <div style={{
      background: "#1A2E4A", borderRadius: 16, border: `1px solid ${B.teal}40`,
      padding: "clamp(20px,4vw,32px)", marginBottom: 24, boxSizing: "border-box"
    }}>
      <SectionHeader num={10} title="EBITDA Impact by OML Maturity" sub="Financial Performance Correlation" />
      
      <p style={{ color: B.lightGray, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
        Technology maturity directly correlates with financial performance. Moving from reactive break-fix to an optimized, AI-ready posture reduces operational drag, minimizes unplanned downtime, and measurably expands EBITDA margins.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {bands.map((band, i) => (
          <motion.div 
            key={i}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.1 }}
            style={{
              background: band.active ? band.color + "15" : "#122240",
              border: `2px solid ${band.active ? band.color : "#1E3A5F"}`,
              borderRadius: 12,
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              gap: 20,
              flexWrap: "wrap",
              position: "relative",
              overflow: "hidden"
            }}
          >
            {band.active && (
              <div style={{
                position: "absolute", top: 0, left: 0, bottom: 0, width: 4, background: band.color
              }} />
            )}
            
            <div style={{ flex: "1 1 200px" }}>
              <div style={{ fontSize: 11, color: B.subText, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 6 }}>
                {band.title}
              </div>
              <div style={{ fontSize: 20, fontWeight: 900, color: band.color, marginBottom: 8 }}>
                {band.impact}
              </div>
              <p style={{ color: band.active ? B.white : B.lightGray, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                {band.desc}
              </p>
            </div>

            {band.active && (
              <div style={{ flexShrink: 0, background: band.color, color: B.white, fontSize: 11, fontWeight: 800, padding: "4px 12px", borderRadius: 6, letterSpacing: 1 }}>
                CURRENT OML
              </div>
            )}
          </motion.div>
        ))}
      </div>

      <div style={{ marginTop: 20, background: "#0D1B2E", borderRadius: 8, padding: "12px 16px", fontSize: 11, color: B.subText, fontStyle: "italic", lineHeight: 1.6 }}>
        <strong>Source Note:</strong> Research from Service Leadership and MIT CISR indicates that organizations with top-quartile technology maturity (OML 4–5) achieve significantly higher profitability and operational efficiency than their reactive peers.
      </div>
    </div>
  );
}